import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import { useResizeObserver, useCopyToClipboard } from 'usehooks-ts';
import { isMobile } from 'react-device-detect';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Input, Modal, Table, Select, DatePicker, Tooltip, Form, Drawer, Switch, notification, InputNumber, Button, Flex } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    ReloadOutlined,
    DeleteOutlined,
    PlusOutlined,
    PrinterOutlined,
    UnorderedListOutlined,
    QuestionCircleOutlined,
    CheckCircleOutlined,
    CopyOutlined,
    EditOutlined,
} from '@ant-design/icons';

import { usePDF } from '@react-pdf/renderer';
import printJS from 'print-js';

import CargoDetails from '@src/components/logistics/cargo-details/cargo-details';

import Comment from '@controls/comment/comment';
import Processing from '@controls/processing';
import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import TruckAcceptanceWithBoxesPdf from '@core/print-forms/truck-acceptance-with-boxes/truck-acceptance-with-boxes-pdf';

import { securityRestriction } from '@src/extensions/notification';
import { exception, info } from '@extensions/notification';
import { getEnumList, delayAction, toFinanceString, getDate, dataURItoBlob, getDateTimeLocal } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { serverFetch } from '@src/server';

import { IUserSession } from '@src/entities/user-session';
import { IInboundCarrierFilter } from '@src/entities/inbound-carrier-filter';
import { IInboundCarrier } from '@src/entities/inbound-carrier';
import { IConsignment } from '@entities/consignment';
import { ICountry } from '@entities/country';
import { IUser } from '@entities/user';
import { IUserFilter } from '@entities/user-filter';

import { InboundCarrierStatus, enumLabel as inboundCarrierStatusLabel } from '@src/enums/inbound-carrier-status';
import { Permission, hasPermission } from '@src/enums/permission';
import { BillStatus } from '@enums/bill-status';
import { CargoCarrierType } from '@enums/cargo-carrier-type';
import { IEnumItem } from '@enums/enum-item';
import { UserType } from '@enums/user-type';

import { AirplaneIcon, TariffIcon, BoxesIcon, TruckIcon } from '@icons/index';

dayjs.extend(utc);

const filterContext: string = 'InboundCarriers';

const InboundCarriers = () => {
    const initFilter: IInboundCarrierFilter = {
        statuses: [InboundCarrierStatus.New, InboundCarrierStatus.OnWay],
        isArchived: false,
    };

    const { RangePicker } = DatePicker;

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [, copy] = useCopyToClipboard();

    const handleCopy = (text: string) => () => {
        copy(text).then(() => {
            info(api, `"${text}" скопирован`);
        });
    };

    const [modal, contextHolder] = Modal.useModal();
    const [api, notificationContextHolder] = notification.useNotification();

    const navigate = useNavigate();
    const resolved = useResolvedPath('');

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const filter = useAppSelector<IInboundCarrierFilter>((s) => s.filters[filterContext]);

    const [carriers, setCarriers] = useState<Array<IInboundCarrier>>([]);
    const [currentCarrier, setCurrentCarrier] = useState<IInboundCarrier>();

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [showFilter, setShowFilter] = useState<boolean>(!isMobile);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [statuses] = useState<Array<IEnumItem>>(getEnumList(InboundCarrierStatus, inboundCarrierStatusLabel));
    const [users, setUsers] = useState<Array<IUser>>([]);

    const [loadingPrintData, setLoadingPrintData] = useState(false);

    const [startAcceptancePrint, setStartAcceptancePrint] = useState<boolean>(false);
    const [printAcceptanceData, setPrintAcceptanceData] = useState<any>();
    const [pdfAcceptanceInstance, updateAcceptancePdf] = usePDF();

    const [startPrint, setStartPrint] = useState<boolean>(false);
    const [openDetails, setOpenDetails] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;

        setCountries([]);
        setCarriers([]);

        const fetchData = async () => {
            setLoading(true);

            const userFilter: IUserFilter = { isActive: true, isArchived: false, types: [UserType.MainClient] };

            const promises = [
                await serverFetch('countries', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('users', { method: 'GET', queryParams: userFilter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения пользователей', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('inboundcarriers', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения рейсов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCountries(result[0][0] || []);
                setUsers(result[0][1] || [])
                setCarriers(result[0][2] || []);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    useEffect(() => {
        if (printAcceptanceData) {
            updateAcceptancePdf(<TruckAcceptanceWithBoxesPdf printData={printAcceptanceData} countries={countries} />);
            setLoadingPrintData(false);
            setStartAcceptancePrint(true);
        }
    }, [printAcceptanceData]);

    useEffect(() => {
        if (startAcceptancePrint && !pdfAcceptanceInstance.loading && pdfAcceptanceInstance.blob) {
            setStartAcceptancePrint(false);
            setPrintAcceptanceData(undefined);

            const blobURL = URL.createObjectURL(pdfAcceptanceInstance.blob);
            printJS(blobURL);
        }
    }, [startAcceptancePrint, pdfAcceptanceInstance]);

    const onDelete = () => {
        if (!currentCarrier) return;

        serverFetch(`inboundcarriers/${currentCarrier.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления рейса', ex, () => d(userLoaded(undefined)));
            });
    };

    const getPrintAcceptanceDataWithBoxes = () => {
        setLoadingPrintData(true);

        serverFetch(`inboundcarriers/${currentCarrier?.id}/print`, { method: 'GET' })
            .then((data) => {
                const printData = {
                    carrierNumber: currentCarrier?.number,
                    consignments: data,
                };

                setPrintAcceptanceData(printData);
            })
            .catch((ex) => {
                setLoadingPrintData(false);
                exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Добавить рейс',
                        key: 'add',
                        type: 'primary',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageInboundCarrier)) {
                                securityRestriction(api, [Permission.ManageInboundCarrier]);
                                return;
                            }

                            navigate(`${resolved.pathname}/new`);
                        },
                    },
                    {
                        label: 'Изменить рейс',
                        key: 'edit',
                        disabled: !currentCarrier,
                        icon: <EditOutlined />,
                        onClick: () => {
                            navigate(`${resolved.pathname}/${currentCarrier?.id}`);
                        },
                    },
                    {
                        label: 'Удалить рейс',
                        key: 'delete',
                        disabled:
                            !currentCarrier ||
                            (currentCarrier.status !== InboundCarrierStatus.New &&
                                !hasPermission(userSession.permissions, Permission.FullAccess)),
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteInboundCarrier)) {
                                securityRestriction(api, [Permission.DeleteInboundCarrier]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить рейс "${currentCarrier?.number}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Детали поставки',
                        key: 'consignments',
                        disabled: !currentCarrier,
                        icon: <UnorderedListOutlined />,
                        onClick: () => {
                            setOpenDetails(true);
                        },
                        hidden: isMobile,
                    },
                    {
                        label: 'Управление грузом',
                        key: 'manageConsignments',
                        disabled: !currentCarrier,
                        icon: <BoxesIcon />,
                        onClick: () => {
                            if (
                                !hasPermission(userSession.permissions, Permission.ManageInboundCarrierConsignment) &&
                                !hasPermission(userSession.permissions, Permission.ViewInboundCarrierConsignment)
                            ) {
                                securityRestriction(api, [
                                    Permission.ManageInboundCarrierConsignment,
                                    Permission.ViewInboundCarrierConsignment,
                                ]);
                                return;
                            }

                            navigate(`${resolved.pathname}/consignments/${currentCarrier?.id}`);
                        },
                        hidden: isMobile,
                    },
                    {
                        label: 'Управление расчетом',
                        key: 'tariffs',
                        disabled: !currentCarrier,
                        icon: <TariffIcon />,
                        onClick: () => {
                            if (
                                !hasPermission(userSession.permissions, Permission.ManageTransportBill) &&
                                !hasPermission(userSession.permissions, Permission.EditTransportBill) &&
                                !hasPermission(userSession.permissions, Permission.ViewTransportBill)
                            ) {
                                securityRestriction(api, [
                                    Permission.ManageTransportBill,
                                    Permission.EditTransportBill,
                                    Permission.ViewTransportBill,
                                ]);
                                return;
                            }

                            navigate(`${resolved.pathname}/tariffs/${currentCarrier?.id}`);
                        },
                        hidden: isMobile,
                    },
                    { isDivider: true },
                    {
                        label: 'Печать',
                        key: 'print',
                        disabled: loading || !currentCarrier || startAcceptancePrint || startPrint,
                        icon: <PrinterOutlined />,
                        hidden: isMobile,
                        children: [
                            {
                                key: 'withoutBoxes',
                                label: 'Без коробок',
                                icon: <PrinterOutlined />,
                                onClick: () => {
                                    setStartPrint(true);

                                    serverFetch(`inboundcarriers/print`, { method: 'POST', bodyData: [currentCarrier?.id] })
                                        .then((data) => {
                                            const fileBody = `data:${data.contentType};base64,${data.fileContents}`;

                                            const blobURL = URL.createObjectURL(dataURItoBlob(fileBody));
                                            printJS(blobURL);

                                            setStartPrint(false);
                                        })
                                        .catch((ex) => {
                                            setStartPrint(false);
                                            exception(api, 'Ошибка выгрузки листа приемки', ex, () => d(userLoaded(undefined)));
                                        });
                                },
                            },
                            {
                                key: 'withBoxes',
                                label: 'С коробками',
                                icon: <PrinterOutlined />,
                                onClick: () => {
                                    getPrintAcceptanceDataWithBoxes();
                                },
                            },
                        ],
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 150 }}
                            key='number'
                            placeholder='Рейс'
                            value={filter?.number}
                            onChange={(data) => {
                                d(setFilter({ ...filter, number: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='users'
                            placeholder='Клиент'
                            maxTagCount='responsive'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, userId: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={users.map((u) => {
                                return { value: u.id, label: u.login };
                            })}
                        />,
                        <Input
                            style={{ width: 180 }}
                            key='legalName'
                            placeholder='Контрагент'
                            value={filter?.legalName}
                            onChange={(data) => {
                                d(setFilter({ ...filter, legalName: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='markingCode'
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='awbNumber'
                            placeholder='AWB'
                            value={filter?.awbNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус рейса'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                        <RangePicker
                            style={{ width: 220 }}
                            allowEmpty={[true, true]}
                            key='etd'
                            format='DD.MM.YYYY'
                            placeholder={['ETD с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[filter?.etdFrom ? dayjs(filter?.etdFrom) : null, filter?.etdTo ? dayjs(filter?.etdTo) : null]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                etdFrom: undefined,
                                                etdTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            etdFrom: value[0]
                                                ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                            etdTo: value[1]
                                                ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                        <RangePicker
                            style={{ width: 220 }}
                            allowEmpty={[true, true]}
                            key='eta'
                            format='DD.MM.YYYY'
                            placeholder={['ETA с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[filter?.etaFrom ? dayjs(filter?.etaFrom) : null, filter?.etaTo ? dayjs(filter?.etaTo) : null]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                etaFrom: undefined,
                                                etaTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            etaFrom: value[0]
                                                ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                            etaTo: value[1]
                                                ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                        <Form.Item key='isArchived' label='Архив' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.isArchived}
                                onChange={(value: any) => {
                                    d(setFilter({ ...filter, statuses: [], isArchived: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const item = carriers.find((o) => o.id === selectedRowKeys[0]);
            setCurrentCarrier(item);
        } else {
            setCurrentCarrier(undefined);
        }

        const items: Array<IConsignment> = [];
        selectedRowKeys.map((e) => {
            const item = carriers.find((o) => o.id === e);
            if (item && item.consignments) items.push(...item.consignments);
        });
    };

    const renderTable = () => {
        const columns: ColumnsType<IInboundCarrier> = [
            {
                title: 'Рейс',
                key: 'number',
                width: 150,
                render: (_, record) => {
                    if (!record.number) return;

                    return (
                        <Flex className='copy-container' align='center' justify='space-between'>
                            <Button
                                variant='link'
                                color='blue'
                                onClick={() => setOpenDetails(true)}
                                style={{ height: 'fit-content', padding: 0 }}
                            >
                                {record.number}
                            </Button>
                            <Tooltip title='Копировать' color='cyan'>
                                <Button
                                    style={{ height: 'fit-content', padding: 0 }}
                                    className='hide-copy'
                                    size='small'
                                    variant='link'
                                    color='cyan'
                                    icon={<CopyOutlined />}
                                    onClick={handleCopy(record.number)}
                                />
                            </Tooltip>
                        </Flex>
                    );
                },
            },
            {
                width: 40,
                align: 'center',
                render: (_, record) => {
                    return record.comment && <Comment comment={record.comment} />;
                },
            },
            {
                title: 'Тип',
                key: 'cargoCarrierType',
                width: 130,
                render: (_, record) => {
                    if (record.cargoCarrierType == CargoCarrierType.Air) {
                        return <AirplaneIcon />;
                    }

                    return <TruckIcon />;
                },
            },
            { title: 'Перевозчик', dataIndex: 'cargoCarrierName', key: 'cargoCarrierName', width: 200 },
            {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (_, record) => {
                    return <>{record.status ? inboundCarrierStatusLabel(record.status) : 'Не назначен'}</>;
                },
            },
            {
                title: 'Пункт отправления',
                dataIndex: 'departurePoint',
                key: 'departurePoint',
                width: 150,
            },
            {
                title: 'ETD',
                width: 150,
                align: 'center',
                render: (_, record) => {
                    return getDateTimeLocal(record.etdOn);
                },
            },
            {
                title: 'ETA',
                width: 150,
                align: 'center',
                render: (_, record) => {
                    return getDateTimeLocal(record.etaOn);
                },
            },
            {
                title: 'Кол-во / шт',
                dataIndex: 'totalQty',
                key: 'totalQty',
                width: 120,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 700,
                    },
                }),
            },
            {
                width: 40,
                align: 'center',
                render: (_, record) => {
                    if (record.billStatus && record.billStatus != BillStatus.New) {
                        return (
                            <Tooltip
                                placement='top'
                                title={
                                    record.billStatus == BillStatus.Completed ||
                                    record.billStatus == BillStatus.Paid ||
                                    record.billStatus == BillStatus.Abandoned
                                        ? 'Расчет Проведен'
                                        : 'Расчет проведен частично'
                                }
                                color={
                                    record.billStatus == BillStatus.Completed ||
                                    record.billStatus == BillStatus.Paid ||
                                    record.billStatus == BillStatus.Abandoned
                                        ? 'var(--main-green)'
                                        : 'darkorange'
                                }
                            >
                                <CheckCircleOutlined
                                    style={{
                                        color:
                                            record.billStatus == BillStatus.Completed ||
                                            record.billStatus == BillStatus.Paid ||
                                            record.billStatus == BillStatus.Abandoned
                                                ? 'var(--main-green)'
                                                : 'darkorange',
                                        fontSize: 18,
                                        marginTop: 5,
                                    }}
                                />
                            </Tooltip>
                        );
                    }

                    return <></>;
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                dataSource={carriers}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                }}
                onRow={(record) => {
                    return {
                        onClick: () => {
                            onSelectChange([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58), x: 'max-content' }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 195px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {currentCarrier && openDetails && (
                <Drawer
                    closable={false}
                    destroyOnHidden
                    title={`Детали поставки "${currentCarrier.number}"`}
                    placement='right'
                    open={true}
                    onClose={() => setOpenDetails(false)}
                    size={1080}
                    extra={
                        <Button variant='link' color='blue' style={{ fontWeight: 600, fontSize: 16 }} onClick={() => setOpenDetails(false)}>
                            Закрыть
                        </Button>
                    }
                >
                    <CargoDetails
                        path='inboundcarriers'
                        truckId={currentCarrier.id || ''}
                        filter={{}}
                        countries={countries}
                        api={api}
                        serverFetch={serverFetch}
                    />
                </Drawer>
            )}

            {(loadingPrintData || startAcceptancePrint || startPrint) && <Processing title='Печать...' />}

            {contextHolder}
            {notificationContextHolder}
        </div>
    );
};

export default InboundCarriers;
